import React, { useContext, useEffect, useReducer, useRef, useCallback, useState } from 'react';
import {
	DevicesContext,
	devicesInitialState,
	IFilterValues,
	TCountOfDevices,
	TDevicesSortBy,
	TTypeOfModalWindow,
} from './devicesContext';
import { devicesReducer } from './devicesReducer';
import { DevicesActionTypes } from './devicesActions';
import { AppContext, IAppContext } from 'appState/appContext';
import { useFetchData } from 'hooks/useFetchData';
import { getPaginatedDevices, deleteDevice } from 'backend/devices';
import { AxiosResponse } from 'axios';
import { IDevices, IGetPaginatedDevices } from 'types';

const DevicesState = ({ children }: JSX.ElementChildrenAttribute) => {
	const [state, dispatch] = useReducer(devicesReducer, devicesInitialState);
	const { showLoader, hideLoader } = useContext<IAppContext>(AppContext);
	const [allowChangingSearchString, setAllowChangingSearchString] = useState(false);
	const isMounted = useRef<boolean>(false);
	const { refetch, response, loading } = useFetchData<AxiosResponse<IDevices>, IGetPaginatedDevices>(
		isMounted.current ? getPaginatedDevices : null,
		{
			pageNumber: state.currentPage,
			pageSize: Number(state.countOfItems),
			searchByName: state.searchString ? state.searchString : null,
			customerId: state.IDSelectedCustomer,
			restaurantId: state.IDSelectedRestaurant,
			sortByCustomerName: state.sortBy === 'CustomerA-Z' || state.sortBy === 'CustomerZ-A' ? state.sortBy : null,
			sortByRestaurantAddress:
				state.sortBy === 'RestaurantA-Z' || state.sortBy === 'RestaurantZ-A' ? state.sortBy : null,
			sortByHardwareId:
				state.sortBy === 'HardwareID1-10' || state.sortBy === 'HardwareID10-1' ? state.sortBy : null,
		},
	);

	useEffect(() => {
		loading ? showLoader() : hideLoader();
	}, [loading]);

	useEffect(() => {
		if (response) {
			dispatch({
				type: DevicesActionTypes.SET_DEVICES,
				payload: {
					devices: response.data,
					typeOfModalWindow: state.typeOfModalWindow,
				},
			});
			setAllowChangingSearchString(true);
		}
	}, [response]);

	const deleteSelectedItem = useCallback(async () => {
		try {
			showLoader();
			await deleteDevice(state.deviceIDForDeletion);
			const filteredDevices = state.devices.items.filter((el) => el.id !== state.deviceIDForDeletion);
			dispatch({
				type: DevicesActionTypes.SET_DEVICES,
				payload: {
					devices: {
						count: state.devices.count - 1,
						items: filteredDevices,
					},
					typeOfModalWindow: 'success',
				},
			});
		} catch (err) {
			dispatch({
				type: DevicesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
				payload: {
					typeOfModalWindow: 'error',
				},
			});
		} finally {
			hideLoader();
		}
	}, [showLoader, hideLoader, state.deviceIDForDeletion, state.devices]);

	const changeSearchString = useCallback(
		(value: string) => {
			if (allowChangingSearchString) {
				dispatch({
					type: DevicesActionTypes.CHANGE_SEARCH_STRING,
					payload: {
						searchString: value,
					},
				});
			}
		},
		[allowChangingSearchString],
	);

	const setIDCustomer = useCallback((value: Nullable<string>) => {
		dispatch({
			type: DevicesActionTypes.SET_ID_CUSTOMER,
			payload: {
				IDSelectedCustomer: value,
			},
		});
	}, []);

	const setIDRestaurant = useCallback((value: Nullable<string>) => {
		dispatch({
			type: DevicesActionTypes.SET_ID_RESTAURANT,
			payload: {
				IDSelectedRestaurant: value,
			},
		});
	}, []);

	const resetSelectedControls = useCallback(() => dispatch({ type: DevicesActionTypes.RESET_SELECTED_CONTROLS }), []);

	const setSortBy = useCallback((value: TDevicesSortBy) => {
		dispatch({
			type: DevicesActionTypes.SET_SORT_BY,
			payload: {
				sortBy: value,
			},
		});
	}, []);

	const setCountOfItems = useCallback((value: TCountOfDevices) => {
		dispatch({
			type: DevicesActionTypes.SET_COUNT_OF_ITEMS,
			payload: {
				countOfItems: value,
			},
		});
	}, []);

	const changeCurrentPage = useCallback((value: number) => {
		dispatch({
			type: DevicesActionTypes.CHANGE_CURRENT_PAGE,
			payload: {
				currentPage: value,
			},
		});
	}, []);

	const setDeviceIDForDeletion = useCallback((value: string) => {
		dispatch({
			type: DevicesActionTypes.SET_DEVICE_ID_FOR_DELETION,
			payload: {
				deviceIDForDeletion: value,
				typeOfModalWindow: 'toConfirmDeleteItem',
			},
		});
	}, []);

	const setDeviceIDForFullInfo = useCallback((value: string) => {
		dispatch({
			type: DevicesActionTypes.SET_DEVICE_ID_FOR_FULL_INFO,
			payload: {
				deviceIDForFullInfo: value,
			},
		});
	}, []);

	const changeTypeOfModalWindow = useCallback((value: TTypeOfModalWindow) => {
		dispatch({
			type: DevicesActionTypes.CHANGE_TYPE_OF_MODAL_WINDOW,
			payload: {
				typeOfModalWindow: value,
			},
		});
	}, []);

	const setFiltersFromQueryString = useCallback(
		(value: IFilterValues) => {
			dispatch({
				type: DevicesActionTypes.SET_FILTERS_FROM_QUERY_STRING,
				payload: {
					filters: value,
				},
			});
			isMounted.current = true;
			refetch();
		},
		[refetch, isMounted],
	);

	return (
		<DevicesContext.Provider
			value={{
				deleteSelectedItem,
				changeSearchString,
				setIDCustomer,
				setIDRestaurant,
				resetSelectedControls,
				setSortBy,
				setCountOfItems,
				changeCurrentPage,
				setDeviceIDForDeletion,
				setDeviceIDForFullInfo,
				changeTypeOfModalWindow,
				setFiltersFromQueryString,
				devices: state.devices,
				searchString: state.searchString,
				IDSelectedCustomer: state.IDSelectedCustomer,
				IDSelectedRestaurant: state.IDSelectedRestaurant,
				sortBy: state.sortBy,
				countOfItems: state.countOfItems,
				currentPage: state.currentPage,
				deviceIDForDeletion: state.deviceIDForDeletion,
				deviceIDForFullInfo: state.deviceIDForFullInfo,
				typeOfModalWindow: state.typeOfModalWindow,
			}}>
			{children}
		</DevicesContext.Provider>
	);
};

export default DevicesState;
